import type { FormStep } from "./form-types";

import { cn } from "@homeinstead/ui";

/**
 * Lead form UI variant. Controls styling (layout, padding, hero).
 *
 * Other variants can be added later.
 */
export type LeadFormVariant = "careSeeker";

export type BreakpointKey = "2xl" | "lg" | "md" | "sm" | "xl";

/** Derived style values for the current step (used by LeadForm when rendering). */
export type LeadFormStepStyles = {
  flexRowClass: string | undefined;
  persistentHeroMinHeightClass: string | undefined;
  wrapperClassName: string;
};

export type LeadFormStyleClasses = {
  continueButtonWrapper: string;
  flexRowBreakpoint: Record<BreakpointKey, string>;
  flexRowBreakpointHero: Record<BreakpointKey, string>;
  persistentHeroMinHeight: Record<BreakpointKey, string>;
  stepBase: string;
  /** Two-column grid from md when an ancestor is `group/lead-form` with `data-layout="buttonStack"`. Always present; inert otherwise. */
  buttonStackHeroGrid: string;
  buttonStackItemsGrid: string;
  stepContentBase: string;
  /** Applied only in hero-wrapper mode to compensate for the wrapper's negative margins. */
  stepContentHeroHorizontalPadding: string;
  /** Hero step vertical breathing room and width. */
  stepContentHeroPadding: string;
  stepGap: string;
  stepperBase: string;
  /**
   * Passed to Stepper's `labelRowClassName` to set the gap below the step count,
   * replacing the shared component's wider default for this form only. The
   * stacked layout mirrors the space above the stepper; the two-column layout
   * tightens it.
   */
  stepperLabelRowSpacing: string;
  /**
   * Extra stepper top padding at the hero breakpoint and up. Stacks with the
   * wrapper's pt-8 (non-hero steps) or stepContentHeroPadding pt-8 (hero step)
   * for 40px total on two-column layouts.
   */
  stepperPaddingTop: string;
  /**
   * Outer wrapper: layout and conditional padding by hero state.
   * `data-[layout=buttonStack]:` is the same alignment at md, and only
   * matches when this element carries `data-layout="buttonStack"`.
   */
  wrapper: string;
  /** Applied on the active hero step only (removes top padding at the layout breakpoint). */
  wrapperHeroFirstStepFlow: string;
  /** Applied when current step is not hero (adds top breathing room). */
  wrapperNonHero: string;
};

const CARE_SEEKER_STYLES: LeadFormStyleClasses = {
  buttonStackHeroGrid:
    "group-data-[layout=buttonStack]/lead-form:md:grid group-data-[layout=buttonStack]/lead-form:md:grid-cols-[2fr_3fr]",
  buttonStackItemsGrid:
    "group-data-[layout=buttonStack]/lead-form:md:grid group-data-[layout=buttonStack]/lead-form:md:grid-cols-2",
  continueButtonWrapper: "mt-4",
  flexRowBreakpoint: {
    "2xl": "2xl:flex-row",
    lg: "lg:flex-row",
    md: "md:flex-row",
    sm: "sm:flex-row",
    // CSS grid with two equal columns for items-mode steps. Hero steps use
    // flexRowBreakpointHero.
    xl: "xl:grid xl:grid-cols-2",
  },
  flexRowBreakpointHero: {
    "2xl": "2xl:flex-row",
    lg: "lg:flex-row",
    md: "md:flex-row",
    sm: "sm:flex-row",
    // CSS grid with [2fr_3fr] columns gives the hero image more room than the
    // form content column, matching the original hero layout proportions.
    xl: "xl:grid xl:grid-cols-[2fr_3fr]",
  },
  // Each entry is `${breakpoint}:min-h-[800px]`. The record is intentional —
  // Tailwind requires complete class strings at build time and cannot scan
  // template literals like `${bp}:min-h-[800px]`.
  persistentHeroMinHeight: {
    "2xl": "2xl:min-h-[800px]",
    lg: "lg:min-h-[800px]",
    md: "md:min-h-[800px]",
    sm: "sm:min-h-[800px]",
    xl: "xl:min-h-[800px]",
  },
  // md:flex restores the step when `max-xl:hidden` would drop it. A grid
  // class later in `cn()` replaces it, because both set display.
  stepBase:
    "flex h-full w-full flex-col group-data-[layout=buttonStack]/lead-form:md:flex",
  stepContentBase:
    "flex flex-col items-center xl:items-start group-data-[layout=buttonStack]/lead-form:md:items-start",
  /** Applied only when the step wrapper is in hero mode (negative margins bleed the
   * wrapper to full viewport width). Compensates for those negative margins so content
   * stays aligned with the surrounding section padding. Non-hero steps rely on the
   * section's own horizontal padding and must not apply this. */
  stepContentHeroHorizontalPadding: "px-4 sm:px-10 lg:px-[72px] 2xl:px-[152px]",
  stepContentHeroPadding:
    "pt-8 pb-8 xl:w-full group-data-[layout=buttonStack]/lead-form:md:w-full",
  stepGap: "gap-4",
  stepperBase:
    "mx-auto w-full md:w-[440px] xl:ml-0 group-data-[layout=buttonStack]/lead-form:md:ml-0",
  stepperLabelRowSpacing:
    "mb-8 xl:mb-6 group-data-[layout=buttonStack]/lead-form:md:mb-6",
  stepperPaddingTop:
    "xl:pt-2 group-data-[layout=buttonStack]/lead-form:md:pt-2",
  wrapper:
    "flex flex-col items-start md:items-center xl:items-start data-[layout=buttonStack]:md:items-start",
  wrapperHeroFirstStepFlow: "xl:pt-0 data-[layout=buttonStack]:md:pt-0",
  wrapperNonHero: "pt-8",
};

/**
 * Returns style class maps for the given variant.
 *
 * Used for hero layout, stack padding, and persistent min-height.
 */
export { CARE_SEEKER_STYLES as defaultLeadFormStyleClasses };

const DEFAULT_BREAKPOINT: BreakpointKey = "xl";

// Always on the step grid. Applies only inside `group/lead-form[data-layout=buttonStack]`.
const BUTTON_STACK_HERO_MIN_HEIGHT =
  "group-data-[layout=buttonStack]/lead-form:md:min-h-[800px]";

/**
 * Resolves a breakpoint key from step config, defaulting to "xl".
 */
export function getBreakpointKey(
  showFromBreakpoint: BreakpointKey | null | undefined,
): BreakpointKey {
  return showFromBreakpoint ?? DEFAULT_BREAKPOINT;
}

/**
 * Derives step-specific class names from variant style classes and current step state.
 * Pure util; call from LeadForm (e.g. in useMemo) with current step, steps, isHeroMode.
 */
export function getLeadFormStepStyles(
  styleClasses: LeadFormStyleClasses,
  currentStep: FormStep,
  steps: FormStep[],
  isHeroMode: boolean,
): LeadFormStepStyles {
  const hasInfoPanel = Boolean(currentStep.infoPanel);
  const heroBreakpoint = getBreakpointKey(
    currentStep.infoPanel?.showFromBreakpoint,
  );
  const firstStepInfoPanel = steps[0]?.infoPanel;
  // Single source of truth: both the persistent min-height and the wrapper
  // top-padding override depend on the same condition. Previously split into
  // two identically-computed booleans; collapsed to avoid silent divergence.
  const isHeroFirstStepFlow = firstStepInfoPanel?.mode === "hero";
  const firstStepBreakpoint = getBreakpointKey(
    firstStepInfoPanel?.showFromBreakpoint,
  );

  const persistentHeroMinHeightClass = isHeroFirstStepFlow
    ? cn(
        styleClasses.persistentHeroMinHeight[firstStepBreakpoint],
        BUTTON_STACK_HERO_MIN_HEIGHT,
      )
    : undefined;
  const flexRowClass = hasInfoPanel
    ? styleClasses.flexRowBreakpoint[heroBreakpoint]
    : undefined;
  const wrapperClassName = cn(styleClasses.wrapper, {
    [styleClasses.wrapperHeroFirstStepFlow]: isHeroFirstStepFlow && isHeroMode,
    [styleClasses.wrapperNonHero]: !isHeroMode,
  });

  return {
    flexRowClass,
    persistentHeroMinHeightClass,
    wrapperClassName,
  };
}
